// API utilities for Docospot
// Handles all HTTP requests, authentication, and error handling

import { API_BASE_URL } from './config.js';
import { auth } from './auth.js';

/**
 * Helper function for consistent API error handling
 * @param {Error} error - The error object
 * @param {string} context - Context/operation name for the error
 */
export function handleApiError(error, context = 'Operation') {
    console.error(`❌ ${context} Error:`, error);
    
    // Don't show error message if it's an auth error (user was auto-logged out)
    if (!error.isAuthError && window.showError) {
        window.showError(`${context} failed: ${error.message || 'Unknown error'}`);
    }
}

/**
 * Main API call function with automatic error handling and auth
 * @param {string} endpoint - API endpoint (e.g., '/users')
 * @param {object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<any>} Response data
 */
export async function apiCall(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        },
        ...options
    };

    try {
        const response = await fetch(url, config);
        
        // Clone the response so we can read it multiple times if needed
        const responseClone = response.clone();
        
        // Check for authentication failures
        if (response.status === 401) {
            console.warn('🔐 Authentication failed - logging out user');

            // Any 401 means the session is invalid — always log out
            let errorDetail = 'Session expired';
            try {
                const data = await response.json();
                if (data.detail) errorDetail = data.detail;
            } catch (e) { /* ignore parse errors */ }

            console.log('🚪 Auto-logout triggered:', errorDetail);
            if (window.autoLogout) {
                window.autoLogout('Your session has expired. Please log in again.');
            }

            const authError = new Error(errorDetail);
            authError.isAuthError = true;
            throw authError;
        }
        
        // Handle 204 No Content responses (successful but no body)
        if (response.status === 204) {
            return null;
        }

        let data;
        try {
            data = await responseClone.json();
        } catch (jsonError) {
            // Response is not valid JSON (likely HTML error page)
            try {
                const text = await response.text();
                console.error('Non-JSON response:', text);
                throw new Error(`Server error (${response.status}): ${response.statusText} - ${text.substring(0, 200)}`);
            } catch (textError) {
                console.error('Could not read response as text either:', textError);
                throw new Error(`Server error (${response.status}): ${response.statusText}`);
            }
        }

        if (!response.ok) {
            let errorMessage = 'API request failed';
            
            // Handle validation errors (array of error objects)
            if (data.detail && Array.isArray(data.detail)) {
                const errors = data.detail.map(err => {
                    if (err.loc && err.msg) {
                        return `${err.loc.join('.')}: ${err.msg}`;
                    }
                    return err.msg || err;
                }).join('; ');
                errorMessage = `Validation error: ${errors}`;
            } else if (data.detail) {
                errorMessage = data.detail;
            } else if (data.message) {
                errorMessage = data.message;
            }
            
            console.error('API Error Details:', data);
            throw new Error(errorMessage);
        }

        return data;
    } catch (error) {
        // Don't log auth errors since they're handled above
        if (!error.isAuthError) {
            console.error('API Error:', error);
        }
        throw error;
    }
}

/**
 * Login with username and password
 * @param {string} username - Username
 * @param {string} password - Password
 * @returns {Promise<object>} Login response with access_token and user
 */
export async function login(username, password) {
    try {
        console.log('🔐 Attempting login with username:', username);
        console.log('🌐 API URL:', `${API_BASE_URL}/auth/login`);
        
        const response = await fetch(`${API_BASE_URL}/auth/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                username: username,
                password: password
            })
        });

        const data = await response.json();
        console.log('📊 Response status:', response.status);
        console.log('📋 Response data:', data);

        if (!response.ok) {
            throw new Error(data.detail || 'Login failed');
        }

        auth.setToken(data.access_token);
        
        // Store user info if provided
        if (data.user) {
            localStorage.setItem('current_user', JSON.stringify(data.user));
            
            // Initialize permissions for the logged-in user
            if (window.permissions) {
                window.permissions.init(data.user);
                console.log('✅ Permissions initialized for role:', data.user.role);
            }
        }
        
        return data;
    } catch (error) {
        console.error('Login Error:', error);
        throw error;
    }
}

/**
 * Login with Microsoft OAuth
 */
export async function loginWithMicrosoft() {
    try {
        if (window.showSuccess) {
            window.showSuccess('Redirecting to Microsoft...');
        }
        // This will redirect to the backend OAuth endpoint
        window.location.href = '/api/auth/microsoft';
    } catch (error) {
        if (window.showError) {
            window.showError('Microsoft login failed: ' + error.message);
        }
    }
}

/**
 * Login with Google OAuth
 */
export async function loginWithGoogle() {
    try {
        if (window.showSuccess) {
            window.showSuccess('Redirecting to Google...');
        }
        // This will redirect to the backend OAuth endpoint
        window.location.href = '/api/auth/google';
    } catch (error) {
        if (window.showError) {
            window.showError('Google login failed: ' + error.message);
        }
    }
}

/**
 * Get current user from API
 * @returns {Promise<object>} Current user data
 */
export async function getCurrentUserFromAPI() {
    return await apiCall('/auth/me', {
        headers: auth.getAuthHeaders()
    });
}

// Legacy Items API calls (for backward compatibility)
/**
 * Get items with optional filters
 * @param {object} params - Query parameters
 * @returns {Promise<object>} Items data
 */
export async function getItems(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/items?${queryString}` : '/items';
    return await apiCall(endpoint);
}

/**
 * Get a single item by ID
 * @param {string} itemId - Item ID
 * @returns {Promise<object>} Item data
 */
export async function getItem(itemId) {
    return await apiCall(`/items/${itemId}`);
}

/**
 * Update an item
 * @param {string} itemId - Item ID
 * @param {object} itemData - Updated item data
 * @returns {Promise<object>} Updated item data
 */
export async function updateItem(itemId, itemData) {
    return await apiCall(`/items/${itemId}`, {
        method: 'PUT',
        headers: auth.getAuthHeaders(),
        body: JSON.stringify(itemData)
    });
}

/**
 * Delete an item
 * @param {string} itemId - Item ID
 * @returns {Promise<object>} Deletion confirmation
 */
export async function deleteItem(itemId) {
    return await apiCall(`/items/${itemId}`, {
        method: 'DELETE',
        headers: auth.getAuthHeaders()
    });
}

/**
 * Get current user's items
 * @returns {Promise<object>} User's items
 */
export async function getMyItems() {
    return await apiCall('/items/user/my-items', {
        headers: auth.getAuthHeaders()
    });
}

// Report Templates API calls
/**
 * Get all report templates
 * @param {object} params - Query parameters (account_id, is_active)
 * @returns {Promise<Array>} Report templates
 */
export async function getReportTemplates(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/report-templates/templates?${queryString}` : '/report-templates/templates';
    return await apiCall(endpoint, {
        headers: auth.getAuthHeaders()
    });
}

/**
 * Get a single report template by ID
 * @param {string} templateId - Template ID
 * @returns {Promise<object>} Template data
 */
export async function getReportTemplate(templateId) {
    return await apiCall(`/report-templates/templates/${templateId}`, {
        headers: auth.getAuthHeaders()
    });
}

/**
 * Create a new report template
 * @param {object} templateData - Template data (name, description, account_id)
 * @returns {Promise<object>} Created template
 */
export async function createReportTemplate(templateData) {
    return await apiCall('/report-templates/templates', {
        method: 'POST',
        headers: auth.getAuthHeaders(),
        body: JSON.stringify(templateData)
    });
}

/**
 * Update a report template
 * @param {string} templateId - Template ID
 * @param {object} templateData - Updated template data
 * @returns {Promise<object>} Updated template
 */
export async function updateReportTemplate(templateId, templateData) {
    return await apiCall(`/report-templates/templates/${templateId}`, {
        method: 'PUT',
        headers: auth.getAuthHeaders(),
        body: JSON.stringify(templateData)
    });
}

/**
 * Delete a report template
 * @param {string} templateId - Template ID
 * @returns {Promise<void>}
 */
export async function deleteReportTemplate(templateId, force = false) {
    const queryString = force ? '?force=true' : '';
    return await apiCall(`/report-templates/templates/${templateId}${queryString}`, {
        method: 'DELETE',
        headers: auth.getAuthHeaders()
    });
}

/**
 * Get template elements for a template
 * @param {string} templateId - Template ID
 * @param {string} page - Optional page filter
 * @returns {Promise<Array>} Template elements
 */
export async function getTemplateElements(templateId, page = null) {
    const queryString = page ? `?page=${page}` : '';
    return await apiCall(`/report-templates/templates/${templateId}/elements${queryString}`, {
        headers: auth.getAuthHeaders()
    });
}

/**
 * Create template elements in bulk
 * @param {string} templateId - Template ID
 * @param {Array} elementsData - Array of element data
 * @returns {Promise<Array>} Created elements
 */
export async function createTemplateElementsBulk(templateId, elementsData) {
    return await apiCall(`/report-templates/templates/${templateId}/elements/bulk`, {
        method: 'POST',
        headers: auth.getAuthHeaders(),
        body: JSON.stringify(elementsData)
    });
}

/**
 * Update a template element
 * @param {string} elementId - Element ID
 * @param {object} elementData - Updated element data
 * @returns {Promise<object>} Updated element
 */
export async function updateTemplateElement(elementId, elementData) {
    return await apiCall(`/report-templates/elements/${elementId}`, {
        method: 'PUT',
        headers: auth.getAuthHeaders(),
        body: JSON.stringify(elementData)
    });
}

/**
 * Delete a template element
 * @param {string} elementId - Element ID
 * @returns {Promise<void>}
 */
export async function deleteTemplateElement(elementId) {
    return await apiCall(`/report-templates/elements/${elementId}`, {
        method: 'DELETE',
        headers: auth.getAuthHeaders()
    });
}

export default {
    apiCall,
    handleApiError,
    login,
    loginWithMicrosoft,
    loginWithGoogle,
    getCurrentUserFromAPI,
    getItems,
    getItem,
    updateItem,
    deleteItem,
    getMyItems,
    // Report Templates
    getReportTemplates,
    getReportTemplate,
    createReportTemplate,
    updateReportTemplate,
    deleteReportTemplate,
    getTemplateElements,
    createTemplateElementsBulk,
    updateTemplateElement,
    deleteTemplateElement
};
